// frontend/src/components/VehicleList.js - Updated with Health Integration
import React, { useState, useEffect } from 'react';
import { 
  Car, Plus, Trash2, AlertTriangle, Gauge, Settings, 
  Wrench, Activity, ChevronRight, RefreshCw, CheckCircle, HelpCircle, Camera
} from 'lucide-react';
import { vehicleHealthAPI, vehicleProfileAPI, vehicleDocumentsAPI, apiUtils } from '../services/api';
import VehicleProfileWizard from './VehicleProfileWizard';
import VehicleHealthDashboard from './VehicleHealthDashboard';
import MileageDisplay from './MileageDisplay';
import { PageHeader, Badge } from './common';
import logger from '../utils/logger';
import CorvinArt from './brand/CorvinArt';

const VehicleList = () => {
  const [vehicles, setVehicles] = useState([]);
  const [vehicleHealthScores, setVehicleHealthScores] = useState({});
  const [vehicleIssuesCount, setVehicleIssuesCount] = useState({});
  const [vehicleProfilePictures, setVehicleProfilePictures] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshSuccess, setRefreshSuccess] = useState(false);
  const [debugMode, setDebugMode] = useState(false);
  const [error, setError] = useState(null);
  const [showAddVehicle, setShowAddVehicle] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);

  useEffect(() => {
    loadVehicles();
  }, []);

  // Auto-refresh health data every 5 minutes if enabled
  useEffect(() => {
    if (!autoRefresh || vehicles.length === 0) return;

    const interval = setInterval(() => {
      logger.log('Auto-refreshing health data...');
      refreshHealthScores();
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(interval);
  }, [autoRefresh, vehicles.length]);

  // Refresh health scores when window gains focus (user returns to tab)
  useEffect(() => {
    const handleFocus = () => {
      if (vehicles.length > 0) {
        // Check if any health data is stale and refresh if needed
        const hasStaleData = vehicles.some(vehicle => {
          const healthData = vehicleHealthScores[vehicle.id];
          return isHealthDataStale(healthData);
        });
        
        if (hasStaleData) {
          logger.log('Detected stale health data, refreshing...');
          refreshHealthScores();
        }
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [vehicles.length, vehicleHealthScores]);

  const loadVehicles = async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (!apiUtils.isAuthenticated()) {
        setError('Please log in to view your vehicles');
        return;
      }

      const vehicleData = await vehicleProfileAPI.getProfiles();
      
      // Debug: Log the response to see what we're getting
      logger.log('Vehicle data received:', vehicleData);
      logger.log('Is array?', Array.isArray(vehicleData));
      logger.log('Type:', typeof vehicleData);
      
      // Ensure vehicleData is always an array
      // Handle case where API might return data wrapped in an object
      let vehiclesArray = [];
      if (vehicleData === null || vehicleData === undefined) {
        console.warn('Vehicle data is null or undefined');
        vehiclesArray = [];
      } else if (Array.isArray(vehicleData)) {
        vehiclesArray = vehicleData;
      } else if (vehicleData && typeof vehicleData === 'object') {
        // Check if data is wrapped in a property (e.g., { vehicles: [...] } or { data: [...] })
        if (Array.isArray(vehicleData.vehicles)) {
          vehiclesArray = vehicleData.vehicles;
        } else if (Array.isArray(vehicleData.data)) {
          vehiclesArray = vehicleData.data;
        } else if (Array.isArray(vehicleData.results)) {
          vehiclesArray = vehicleData.results;
        } else {
          console.warn('Vehicle data is not an array and no known array property found:', vehicleData);
          vehiclesArray = [];
        }
      } else {
        console.warn('Vehicle data is not an array or object:', vehicleData);
        vehiclesArray = [];
      }
      
      logger.log('Vehicles array after processing:', vehiclesArray);
      setVehicles(vehiclesArray);
      
      // Load health scores for each vehicle
      if (vehiclesArray.length > 0) {
        loadHealthScores(vehiclesArray);
        loadVehicleIssuesCount(vehiclesArray); // Load issues count
        loadProfilePictures(vehiclesArray); // Load profile pictures
      }
    } catch (err) {
      console.error('Error loading vehicles:', err);
      setError(apiUtils.formatErrorMessage(err));
      // Ensure vehicles is set to empty array on error
      setVehicles([]);
    } finally {
      setLoading(false);
    }
  };

  const loadHealthScores = async (vehicles) => {
    const scores = {};
    
    // Load health scores in parallel
    await Promise.all(
      vehicles.map(async (vehicle) => {
        try {
          const healthData = await vehicleHealthAPI.getHealthScore(vehicle.id);
          scores[vehicle.id] = healthData;
        } catch (err) {
          console.error(`Failed to load health score for vehicle ${vehicle.id}:`, err);
          scores[vehicle.id] = null;
        }
      })
    );
    
    setVehicleHealthScores(scores);
    setLastRefreshed(new Date());
  };

  const loadVehicleIssuesCount = async (vehicles) => {
    const counts = {};
    
    // Load vehicle issues count in parallel
    await Promise.all(
      vehicles.map(async (vehicle) => {
        try {
          const issues = await vehicleHealthAPI.getVehicleIssues(vehicle.id);
          // Ensure issues is always an array before filtering
          const issuesArray = Array.isArray(issues) ? issues : [];
          // Count only unresolved issues (status !== 'resolved')
          const unresolvedCount = issuesArray.filter(issue => issue.status !== 'resolved').length;
          counts[vehicle.id] = unresolvedCount;
        } catch (err) {
          console.error(`Failed to load issues count for vehicle ${vehicle.id}:`, err);
          counts[vehicle.id] = 0;
        }
      })
    );
    
    setVehicleIssuesCount(counts);
  };

  const loadProfilePictures = async (vehicles) => {
    const pictures = {};
    
    // Load profile pictures in parallel
    await Promise.all(
      vehicles.map(async (vehicle) => {
        try {
          const response = await vehicleDocumentsAPI.getProfilePicture(vehicle.id);
          const profilePic = response.profile_picture || response;
          pictures[vehicle.id] = profilePic;
        } catch (err) {
          console.error(`Failed to load profile picture for vehicle ${vehicle.id}:`, err);
          pictures[vehicle.id] = null;
        }
      })
    );
    
    setVehicleProfilePictures(pictures);
  };

  const refreshHealthScores = async () => {
    if (vehicles.length > 0) {
      try {
        setRefreshing(true);
        setError(null);
        setRefreshSuccess(false);
        await Promise.all([
          loadHealthScores(vehicles),
          loadVehicleIssuesCount(vehicles),
          loadProfilePictures(vehicles)
        ]);
        setLastRefreshed(new Date());
        setRefreshSuccess(true);
        // Clear success message after 3 seconds
        setTimeout(() => setRefreshSuccess(false), 3000);
      } catch (err) {
        console.error('Error refreshing health scores:', err);
        setError('Failed to refresh health data: ' + apiUtils.formatErrorMessage(err));
      } finally {
        setRefreshing(false);
      }
    }
  };

  const forceRefreshData = async () => {
    if (vehicles.length > 0) {
      try {
        setRefreshing(true);
        setError(null);
        setRefreshSuccess(false);
        
        // Force refresh by adding timestamp to bypass cache
        const timestamp = Date.now();
        await Promise.all([
          loadHealthScores(vehicles),
          loadVehicleIssuesCount(vehicles),
          loadProfilePictures(vehicles)
        ]);
        
        setLastRefreshed(new Date());
        setRefreshSuccess(true);
        setTimeout(() => setRefreshSuccess(false), 3000);
      } catch (err) {
        console.error('Error force refreshing data:', err);
        setError('Failed to force refresh data: ' + apiUtils.formatErrorMessage(err));
      } finally {
        setRefreshing(false);
      }
    }
  };

  const handleAddVehicle = () => {
    setShowAddVehicle(true);
  };

  const handleVehicleCreated = (newVehicle) => {
    setVehicles(prev => [newVehicle, ...prev]);
    setShowAddVehicle(false);
    // Load health score for new vehicle
    loadHealthScores([newVehicle]);
    loadVehicleIssuesCount([newVehicle]); // Load issues count for new vehicle
    loadProfilePictures([newVehicle]); // Load profile picture for new vehicle
  };

  const handleDeleteVehicle = async (vehicleId) => {
    if (!window.confirm('Are you sure you want to delete this vehicle profile?')) {
      return;
    }

    try {
      await vehicleProfileAPI.deleteProfile(vehicleId);
      setVehicles(prev => prev.filter(v => v.id !== vehicleId));
    } catch (err) {
      console.error('Error deleting vehicle:', err);
      alert('Failed to delete vehicle: ' + apiUtils.formatErrorMessage(err));
    }
  };

  const handleSelectVehicle = (vehicle) => {
    setSelectedVehicle(vehicle);
  };

  const handleBackFromDashboard = () => {
    setSelectedVehicle(null);
    // Refresh health scores and issues count when returning from dashboard
    if (vehicles.length > 0) {
      Promise.all([
        loadHealthScores(vehicles),
        loadVehicleIssuesCount(vehicles),
        loadProfilePictures(vehicles)
      ]);
    }
  };

  const getHealthColor = (score) => {
    if (!score) return 'bg-surface-muted text-ink-muted';
    if (score >= 80) return 'bg-success-tint text-success';
    if (score >= 60) return 'bg-warning-tint text-warning';
    if (score >= 40) return 'bg-warning-tint text-warning';
    return 'bg-danger-tint text-danger';
  };

  const isHealthDataStale = (healthData) => {
    if (!healthData || !healthData.calculated_at) return true;
    const lastCalculated = new Date(healthData.calculated_at);
    const now = new Date();
    const hoursSinceUpdate = (now - lastCalculated) / (1000 * 60 * 60);
    return hoursSinceUpdate > 24; // Consider stale if older than 24 hours
  };

  // Status is a tinted badge, not a coloured fill (DESIGN_SYSTEM.md).
  const getHealthBadge = (status) => ({
    excellent: 'success',
    good: 'success',
    fair: 'warning',
    poor: 'danger',
    critical: 'danger',
  }[status] || 'neutral');

  // Show vehicle health dashboard if a vehicle is selected
  if (selectedVehicle) {
    return (
      <VehicleHealthDashboard
        vehicle={selectedVehicle}
        profilePicture={vehicleProfilePictures[selectedVehicle.id]}
        onBack={handleBackFromDashboard}
        onHealthDataUpdate={refreshHealthScores}
      />
    );
  }

  // Show add vehicle wizard
  if (showAddVehicle) {
    return (
      <VehicleProfileWizard
        onComplete={handleVehicleCreated}
        onCancel={() => setShowAddVehicle(false)}
      />
    );
  }

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
          <span className="ml-2 text-ink-muted">Loading your vehicles...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <div className="bg-danger-tint border border-danger rounded-lg p-4">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 text-danger mr-2" />
            <span className="text-danger">Error loading vehicles: {error}</span>
          </div>
          <button
            onClick={loadVehicles}
            className="mt-2 text-danger hover:text-danger font-medium"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Check if we're in development mode
  const isDevelopment = process.env.NODE_ENV === 'development';

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6 sm:mb-8">
        <div className="flex-1">
          <PageHeader
            title="My Vehicles"
            subtitle="Manage your vehicle profiles and track maintenance"
          />
          {lastRefreshed && (
            <p className="text-xs text-ink-muted mt-1">
              Health data last updated: {lastRefreshed.toLocaleString()}
            </p>
          )}
          {autoRefresh && isDevelopment && (
            <p className="text-xs text-accent mt-1 flex items-center">
              <Activity className="w-3 h-3 mr-1" />
              Auto-refresh enabled (every 5 minutes)
            </p>
          )}
          {refreshSuccess && (
            <p className="text-xs text-success mt-1 flex items-center">
              <CheckCircle className="w-3 h-3 mr-1" />
              Health data refreshed successfully!
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          {/* Refresh button - always visible */}
          <button
            onClick={refreshHealthScores}
            aria-label={refreshing ? 'Refreshing' : 'Refresh'}
            className="flex items-center px-3 sm:px-4 py-2 bg-surface-muted text-ink-soft hover:bg-surface-muted rounded-lg transition-colors border border-border-default text-sm"
            title="Refresh health data"
            disabled={refreshing}
          >
            {refreshing ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-accent mr-2"></div>
            ) : (
              <RefreshCw className="w-4 h-4 sm:mr-2" />
            )}
            <span className="hidden sm:inline">{refreshing ? 'Refreshing...' : 'Refresh'}</span>
          </button>
          
          {/* Debug buttons - only visible in development */}
          {isDevelopment && (
            <>
              <button
                onClick={forceRefreshData}
                className="flex items-center px-3 py-2 bg-warning-tint text-warning hover:bg-warning-tint/80 rounded-lg transition-colors border border-warning-border text-sm"
                title="Force refresh health data (bypasses cache)"
                disabled={refreshing}
              >
                <RefreshCw className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Force Refresh</span>
              </button>
              <button
                onClick={() => setAutoRefresh(!autoRefresh)}
                className={`flex items-center px-3 py-2 rounded-lg transition-colors border text-sm ${
                  autoRefresh
                    ? 'bg-accent-tint text-accent border-accent-border'
                    : 'bg-surface-muted text-ink-soft border-border-default hover:bg-surface-muted'
                }`}
                title={autoRefresh ? 'Disable auto-refresh' : 'Enable auto-refresh every 5 minutes'}
              >
                <Activity className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">{autoRefresh ? 'Auto-ON' : 'Auto-OFF'}</span>
              </button>
              <button
                onClick={() => setDebugMode(!debugMode)}
                className={`flex items-center px-3 py-2 rounded-lg transition-colors border text-sm ${
                  debugMode
                    ? 'bg-info-tint text-info border-info-border'
                    : 'bg-surface-muted text-ink-soft border-border-default hover:bg-surface-muted'
                }`}
                title={debugMode ? 'Disable debug mode' : 'Enable debug mode to see data details'}
              >
                <Settings className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">{debugMode ? 'Debug ON' : 'Debug OFF'}</span>
              </button>
            </>
          )}
          
          {/* Add Vehicle button - always visible */}
          <button
            onClick={handleAddVehicle}
            aria-label="Add Vehicle"
            className="flex items-center px-3 sm:px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent-hover transition-colors text-sm"
          >
            <Plus className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Add Vehicle</span>
          </button>
        </div>
      </div>

      {/* No vehicles state */}
      {vehicles.length === 0 ? (
        <div className="text-center py-12">
          <CorvinArt pose="pointing" size={112} className="mx-auto mb-4" />
          <h3 className="text-section-title font-semibold text-ink-soft mb-2">No vehicles yet</h3>
          <p className="text-ink-muted mb-6">
            Add one with a VIN, or a year, make and model, and I'll keep track of what's due.
          </p>
          <button
            onClick={handleAddVehicle}
            className="px-6 py-3 bg-accent text-white rounded-lg hover:bg-accent-hover transition-colors"
          >
            Add Your First Vehicle
          </button>
        </div>
      ) : (
        /* Vehicle Grid */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {vehicles.map((vehicle) => {
            const healthData = vehicleHealthScores[vehicle.id];
            const healthStatus = healthData ? vehicleHealthAPI.getHealthStatusInfo(healthData.status) : null;
            const profilePicture = vehicleProfilePictures[vehicle.id];
            
            return (
              <div
                key={vehicle.id}
                className="bg-surface-card rounded-card border border-border-default shadow-elevation-1 hover:shadow-elevation-2 transition-shadow cursor-pointer"
                onClick={() => handleSelectVehicle(vehicle)}
              >
                <div className="p-4 sm:p-6 pb-4">
                  <div className="flex items-start justify-between mb-4 gap-2">
                    <div className="flex items-center min-w-0 flex-1">
                      {profilePicture ? (
                        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl overflow-hidden mr-3 border-2 border-border-default flex-shrink-0">
                          <img
                            src={profilePicture.url || profilePicture}
                            alt="Vehicle profile"
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ) : (
                        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-accent/10 rounded-xl flex items-center justify-center mr-3 flex-shrink-0">
                          <Car className="w-5 h-5 sm:w-6 sm:h-6 text-accent" />
                        </div>
                      )}
                      <div className="min-w-0">
                        <h3 className="font-semibold text-ink-soft text-base sm:text-lg truncate">
                          {vehicle.display_name || `${vehicle.year} ${vehicle.make} ${vehicle.model}`}
                        </h3>
                        <p className="text-xs sm:text-sm text-ink-muted truncate">
                          {vehicle.year} {vehicle.make} {vehicle.model}
                          {vehicle.trim && ` ${vehicle.trim}`}
                        </p>
                      </div>
                    </div>
                    
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteVehicle(vehicle.id);
                      }}
                      className="p-2 text-ink-muted hover:text-danger rounded-lg hover:bg-surface-page flex-shrink-0"
                      title="Delete vehicle"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Health Score Card */}
                  {healthData && (
                    <div className="mb-3 sm:mb-4 p-3 rounded-control bg-surface-sunken border border-border-default">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="text-label uppercase text-ink-muted">Health Score</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xl sm:text-2xl font-mono tabular-nums font-semibold text-ink">{healthData.score}</span>
                            <Badge status={getHealthBadge(healthData.status)}>{healthStatus.text}</Badge>
                          </div>
                        </div>
                        {isHealthDataStale(healthData) && (
                          <Badge status="warning">
                            <AlertTriangle className="w-3 h-3" strokeWidth={1.5} aria-hidden="true" />
                            Stale
                          </Badge>
                        )}
                      </div>
                      <p className="text-small text-ink-muted mt-2 hidden sm:block">
                        Based on maintenance history, vehicle age, and overall condition
                      </p>
                    </div>
                  )}

                  {/* Quick Stats */}
                  <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-3 sm:mb-4">
                    <div>
                      <p className="text-xs sm:text-sm text-ink-muted mb-1">Mileage</p>
                      <MileageDisplay 
                        mileage={vehicle.mileage || 0} 
                        showUnitToggle={true}
                        size="normal"
                      />
                    </div>
                    
                    {healthData && (
                      <div>
                        <div className="flex items-center">
                          <p className="text-xs sm:text-sm text-ink-muted">Reported Issues</p>
                          <HelpCircle 
                            className="w-3 h-3 ml-1 text-ink-muted cursor-help" 
                            title="Issues you've reported that haven't been resolved yet"
                          />
                        </div>
                        <p className="font-mono tabular-nums font-semibold text-ink text-body">
                          {vehicleIssuesCount[vehicle.id] || 0}
                        </p>
                        {isHealthDataStale(healthData) && (
                          <p className="text-small text-warning mt-1 flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" strokeWidth={1.5} aria-hidden="true" />
                            Outdated
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Critical Issues Alert */}
                  {healthData?.factors?.critical_issues > 0 && (
                    <div className="mb-3 sm:mb-4 p-2 bg-danger-tint border border-danger rounded-lg">
                      <p className="text-xs font-medium text-danger flex items-center">
                        <AlertTriangle className="w-3 h-3 mr-1 flex-shrink-0" />
                        <span>{healthData.factors.critical_issues} critical issue{healthData.factors.critical_issues > 1 ? 's' : ''} need attention</span>
                      </p>
                    </div>
                  )}

                  {/* Next Maintenance */}
                  {vehicle.next_maintenance && (
                    <div className="mb-3 sm:mb-4 p-2 sm:p-3 bg-accent/5 border border-accent/20 rounded-card">
                      <h4 className="text-xs sm:text-sm font-medium text-accent mb-1">Next Service</h4>
                      <p className="text-xs sm:text-sm text-accent">{vehicle.next_maintenance.type}</p>
                      {vehicle.next_maintenance.due_mileage && (
                        <p className="text-xs text-accent/70">
                          Due at {vehicle.next_maintenance.due_mileage.toLocaleString()} miles
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="px-4 sm:px-6 py-3 bg-surface-page rounded-b-card">
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-ink-muted">
                      {healthData ? (
                        <div>Last checked: {new Date(healthData.calculated_at).toLocaleDateString()}</div>
                      ) : (
                        <div>Added {new Date(vehicle.created_at).toLocaleDateString()}</div>
                      )}
                    </div>
                    
                    <ChevronRight className="w-4 h-4 text-ink-muted" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Debug Information - only in development */}
      {isDevelopment && debugMode && (
        <div className="mt-6 p-4 bg-surface-page border border-border-default rounded-lg">
          <h3 className="text-card-title font-semibold text-ink-soft mb-3">Debug Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <h4 className="font-medium text-ink-soft mb-2">Health Score Data</h4>
              <pre className="bg-surface-card p-2 rounded border text-xs overflow-auto max-h-48">
                {JSON.stringify(vehicleHealthScores, null, 2)}
              </pre>
            </div>
            <div>
              <h4 className="font-medium text-ink-soft mb-2">Vehicle Issues Count</h4>
              <pre className="bg-surface-card p-2 rounded border text-xs overflow-auto max-h-48">
                {JSON.stringify(vehicleIssuesCount, null, 2)}
              </pre>
            </div>
            <div>
              <h4 className="font-medium text-ink-soft mb-2">Last Refreshed</h4>
              <p className="text-ink-muted">
                {lastRefreshed ? lastRefreshed.toLocaleString() : 'Never'}
              </p>
            </div>
            <div>
              <h4 className="font-medium text-ink-soft mb-2">Auto-refresh Status</h4>
              <p className="text-ink-muted">
                {autoRefresh ? 'Enabled (every 5 minutes)' : 'Disabled'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Add more vehicles button */}
      {vehicles.length > 0 && (
        <div className="mt-8 text-center">
          <button
            onClick={handleAddVehicle}
            className="inline-flex items-center px-4 py-2 border border-border-default rounded-card text-ink-soft bg-surface-card hover:bg-surface-page transition-colors"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Another Vehicle
          </button>
        </div>
      )}
    </div>
  );
};

export default VehicleList;